import { spawn } from "node:child_process";
import { createConnection, type Socket } from "node:net";
import { fileURLToPath } from "node:url";

import type { ExtensionContext } from "@earendil-works/pi-coding-agent";
import type { WorkingMemorySnapshot } from "@pi-mentis/pi-mentis-memory-core";
import {
  createPiCognitionReasoner,
  createPiPairwiseRelationshipReasoner,
} from "@pi-mentis/pi-mentis-pi-extension-support";

import {
  SIDECAR_PROTOCOL_VERSION,
  type MemoryCapsule,
  type PiScopeContext,
  type SessionOpenResult,
  type SidecarEventMessage,
  type SidecarInboundMessage,
  type SidecarMethodResult,
  type SidecarNotification,
  type SidecarOutboundMessage,
  type SidecarRequest,
} from "./sidecar-protocol.js";
import { sidecarEndpoint } from "./sidecar-endpoint.js";
import { encodeSidecarFrame, SidecarFrameDecoder } from "./sidecar-wire.js";

interface PendingRequest {
  readonly resolve: (value: unknown) => void;
  readonly reject: (error: Error) => void;
  readonly timer: ReturnType<typeof setTimeout>;
}

type SessionOpenParams = Extract<SidecarRequest, { readonly method: "session.open" }>["params"];

export type MentisSidecarLifecycleState =
  "stopped" | "starting" | "ready" | "restarting" | "stopping" | "closed";

export interface MentisSidecarClientOptions {
  readonly requestTimeoutMs?: number;
  readonly restartBaseDelayMs?: number;
  readonly restartMaxDelayMs?: number;
  readonly onCapsule?: (capsule: MemoryCapsule) => void;
  readonly onActiveContext?: (clientSessionId: string, snapshot: WorkingMemorySnapshot) => void;
  readonly onWarning?: (message: string) => void;
  readonly onScopeContext?: (scopeContext: PiScopeContext) => void;
  readonly onStateChange?: (state: MentisSidecarLifecycleState) => void;
  /** Test seams; production callers use the per-user machine singleton endpoint. */
  readonly endpoint?: string;
  readonly connect?: (endpoint: string) => Socket;
  readonly launch?: (entry: string, endpoint: string) => void;
}

function sessionKey(input: SessionOpenParams | undefined): string | undefined {
  if (input === undefined) return undefined;
  return JSON.stringify([
    input.clientSessionId,
    input.cwd,
    input.branchId,
    input.branchGeneration,
    input.parentBranchId ?? "",
    input.sessionMode,
  ]);
}

export class MentisSidecarClient {
  readonly #requestTimeoutMs: number;
  readonly #restartBaseDelayMs: number;
  readonly #restartMaxDelayMs: number;
  readonly #onCapsule: (capsule: MemoryCapsule) => void;
  readonly #onActiveContext: (clientSessionId: string, snapshot: WorkingMemorySnapshot) => void;
  readonly #onWarning: (message: string) => void;
  readonly #onScopeContext: (scopeContext: PiScopeContext) => void;
  readonly #onStateChange: (state: MentisSidecarLifecycleState) => void;
  readonly #endpoint: string;
  readonly #connectSocket: (endpoint: string) => Socket;
  readonly #launchSidecar: (entry: string, endpoint: string) => void;
  readonly #pending = new Map<string, PendingRequest>();
  readonly #notificationBacklog: SidecarNotification[] = [];
  readonly #reasonControllers = new Map<string, AbortController>();
  #socket: Socket | undefined;
  #serverPid: number | undefined;
  #decoder = new SidecarFrameDecoder();
  #connecting: Promise<void> | undefined;
  #sequence = 0;
  #ready = false;
  #initialized = false;
  #closed = false;
  #desiredRunning = false;
  #restartAttempt = 0;
  #restartTimer: ReturnType<typeof setTimeout> | undefined;
  #running: Promise<SessionOpenResult | undefined> | undefined;
  #state: MentisSidecarLifecycleState = "stopped";
  #initialization: { readonly cwd: string; readonly piPackageRoot: string } | undefined;
  #sessionOpen: SessionOpenParams | undefined;
  #activeSessionKey: string | undefined;
  #lastSessionOpenResult: SessionOpenResult | undefined;
  #reasonContext: Pick<ExtensionContext, "model" | "modelRegistry"> | undefined;

  constructor(options: MentisSidecarClientOptions = {}) {
    this.#requestTimeoutMs = options.requestTimeoutMs ?? 15_000;
    this.#restartBaseDelayMs = Math.max(10, options.restartBaseDelayMs ?? 250);
    this.#restartMaxDelayMs = Math.max(
      this.#restartBaseDelayMs,
      options.restartMaxDelayMs ?? 5_000,
    );
    this.#onCapsule = options.onCapsule ?? (() => undefined);
    this.#onActiveContext = options.onActiveContext ?? (() => undefined);
    this.#onWarning = options.onWarning ?? (() => undefined);
    this.#onScopeContext = options.onScopeContext ?? (() => undefined);
    this.#onStateChange = options.onStateChange ?? (() => undefined);
    this.#endpoint = options.endpoint ?? sidecarEndpoint();
    this.#connectSocket = options.connect ?? ((endpoint) => createConnection(endpoint));
    this.#launchSidecar =
      options.launch ??
      ((entry, endpoint) => {
        const child = spawn(process.execPath, [entry], {
          stdio: "ignore",
          detached: true,
          env: {
            ...process.env,
            PI_MENTIS_SIDECAR: "1",
            PI_MENTIS_SIDECAR_SOCKET: endpoint,
          },
        });
        child.once("error", (error) => {
          this.#onWarning(`Mentis sidecar launch failed: ${error.message}`);
        });
        child.unref();
      });
  }

  get state(): MentisSidecarLifecycleState {
    return this.#state;
  }

  get pid(): number | undefined {
    return this.#serverPid;
  }

  setReasonContext(context: Pick<ExtensionContext, "model" | "modelRegistry"> | undefined): void {
    this.#reasonContext = context;
  }

  cancelReasoning(): void {
    for (const controller of this.#reasonControllers.values()) controller.abort();
    this.#reasonControllers.clear();
  }

  /**
   * Attach to the per-user machine Sidecar, starting it only when the singleton
   * endpoint is absent, then open this Pi client's current session.
   */
  async start(
    cwd: string,
    piPackageRoot: string,
    sessionOpen?: SessionOpenParams,
  ): Promise<SessionOpenResult | undefined> {
    if (this.#closed) throw new Error("Mentis sidecar client is closed");
    this.#desiredRunning = true;
    this.#initialization = { cwd, piPackageRoot };
    if (sessionOpen !== undefined) this.#sessionOpen = sessionOpen;
    this.#clearRestartTimer();
    return await this.#ensureRunning();
  }

  async call<M extends SidecarRequest["method"]>(
    method: M,
    params: Extract<SidecarRequest, { readonly method: M }>["params"],
    timeoutMs = this.#requestTimeoutMs,
  ): Promise<SidecarMethodResult<M>> {
    if (this.#closed) throw new Error("Mentis sidecar client is closed");
    if (method === "session.open") {
      this.#sessionOpen = params as SessionOpenParams;
    }
    if (method !== "initialize") {
      this.#desiredRunning = true;
      await this.#ensureRunning();
    }
    if (method === "session.open") {
      const result = await this.#ensureSessionOpen();
      return result as SidecarMethodResult<M>;
    }
    return (await this.#request(
      { method, params } as SidecarRequest,
      timeoutMs,
    )) as SidecarMethodResult<M>;
  }

  notify(notification: SidecarNotification): void {
    if (this.#closed) return;
    if (notification.method === "input.activity") this.cancelReasoning();
    if (
      notification.method === "session.branch" &&
      this.#sessionOpen?.clientSessionId === notification.params.clientSessionId
    ) {
      this.#sessionOpen = {
        clientSessionId: this.#sessionOpen.clientSessionId,
        cwd: this.#sessionOpen.cwd,
        sessionMode: this.#sessionOpen.sessionMode,
        branchId: notification.params.branchId,
        branchGeneration: notification.params.branchGeneration,
        ...(notification.params.parentBranchId === undefined
          ? {}
          : { parentBranchId: notification.params.parentBranchId }),
      };
      if (this.#ready) this.#activeSessionKey = sessionKey(this.#sessionOpen);
    }
    if (!this.#connected() || !this.#ready) {
      this.#notificationBacklog.push(notification);
      if (this.#notificationBacklog.length > 512) this.#notificationBacklog.shift();
      this.#desiredRunning = true;
      if (this.#initialization !== undefined) {
        void this.#ensureRunning().catch((error) => {
          if (this.#closed || !this.#desiredRunning) return;
          this.#onWarning(
            `Mentis sidecar demand start failed: ${
              error instanceof Error ? error.message : String(error)
            }`,
          );
          this.#scheduleRestart();
        });
      }
      return;
    }
    this.#send({
      type: "notification",
      protocolVersion: SIDECAR_PROTOCOL_VERSION,
      notification,
    });
  }

  async close(): Promise<void> {
    if (this.#closed) return;
    this.#closed = true;
    this.#desiredRunning = false;
    this.#setState("stopping");
    this.#clearRestartTimer();
    this.cancelReasoning();
    const socket = this.#socket;
    if (socket !== undefined && !socket.destroyed) {
      socket.end();
      await new Promise<void>((resolve) => {
        const timer = setTimeout(resolve, 1_000);
        socket.once("close", () => {
          clearTimeout(timer);
          resolve();
        });
      });
    }
    socket?.destroy();
    if (this.#socket === socket) this.#socket = undefined;
    this.#serverPid = undefined;
    this.#ready = false;
    this.#initialized = false;
    this.#activeSessionKey = undefined;
    this.#notificationBacklog.length = 0;
    this.#rejectPending(new Error("Mentis sidecar closed"));
    this.#setState("closed");
  }

  /**
   * Restart the Sidecar in place so it reloads the complete Mentis config and
   * reopens the current Pi session. This is used only by the local settings
   * control plane; no configuration values travel over IPC.
   */
  async restart(): Promise<SessionOpenResult | undefined> {
    if (this.#closed) throw new Error("Mentis sidecar client is closed");
    if (this.#initialization === undefined) {
      throw new Error("Mentis sidecar initialization is unavailable");
    }
    this.#desiredRunning = false;
    this.#clearRestartTimer();
    this.cancelReasoning();
    const socket = this.#socket;
    if (this.#connected()) {
      await this.#request({ method: "shutdown", params: {} }, 5_000).catch(() => undefined);
      await new Promise<void>((resolve) => {
        const timer = setTimeout(resolve, 1_000);
        socket?.once("close", () => {
          clearTimeout(timer);
          resolve();
        });
      });
    }
    socket?.destroy();
    if (this.#socket === socket) this.#socket = undefined;
    this.#serverPid = undefined;
    this.#ready = false;
    this.#initialized = false;
    this.#activeSessionKey = undefined;
    this.#lastSessionOpenResult = undefined;
    this.#rejectPending(new Error("Mentis sidecar is restarting after a settings change"));
    this.#desiredRunning = true;
    this.#restartAttempt = 1;
    this.#setState("restarting");
    return await this.#ensureRunning();
  }

  async #ensureRunning(): Promise<SessionOpenResult | undefined> {
    const desiredSessionKey = sessionKey(this.#sessionOpen);
    if (
      this.#ready &&
      this.#initialized &&
      this.#connected() &&
      this.#activeSessionKey === desiredSessionKey
    ) {
      return this.#lastSessionOpenResult;
    }
    if (this.#running !== undefined) {
      await this.#running;
      if (this.#activeSessionKey !== sessionKey(this.#sessionOpen)) {
        return await this.#ensureRunning();
      }
      return this.#lastSessionOpenResult;
    }
    const initialization = this.#initialization;
    if (initialization === undefined) {
      throw new Error("Mentis sidecar initialization is unavailable");
    }
    this.#setState(this.#restartAttempt > 0 ? "restarting" : "starting");
    this.#running = (async () => {
      await this.#connect();
      if (!this.#initialized) {
        await this.#request({ method: "initialize", params: initialization }, 30_000);
        this.#initialized = true;
      }
      const opened = await this.#ensureSessionOpen();
      this.#ready = true;
      this.#restartAttempt = 0;
      this.#setState("ready");
      this.#flushNotifications();
      return opened;
    })().finally(() => {
      this.#running = undefined;
    });
    try {
      const opened = await this.#running;
      if (this.#activeSessionKey !== sessionKey(this.#sessionOpen)) {
        return await this.#ensureRunning();
      }
      return opened;
    } catch (error) {
      this.#ready = false;
      throw error;
    }
  }

  async #ensureSessionOpen(): Promise<SessionOpenResult | undefined> {
    const input = this.#sessionOpen;
    const key = sessionKey(input);
    if (input === undefined || key === undefined) return undefined;
    if (this.#activeSessionKey === key) return this.#lastSessionOpenResult;
    const result = (await this.#request(
      { method: "session.open", params: input },
      30_000,
    )) as SessionOpenResult;
    if (sessionKey(this.#sessionOpen) === key) {
      this.#activeSessionKey = key;
      this.#lastSessionOpenResult = result;
      this.#onScopeContext(result.scopeContext);
      if (result.capsule !== undefined) this.#onCapsule(result.capsule);
    }
    return result;
  }

  async #request(request: SidecarRequest, timeoutMs: number): Promise<unknown> {
    if (!this.#connected()) await this.#connect();
    const id = `rpc:${process.pid}:${++this.#sequence}`;
    const method = request.method;
    const message: SidecarOutboundMessage = {
      type: "request",
      id,
      protocolVersion: SIDECAR_PROTOCOL_VERSION,
      request,
    };
    return await new Promise<unknown>((resolve, reject) => {
      const timer = setTimeout(() => {
        this.#pending.delete(id);
        reject(new Error(`Mentis sidecar ${method} exceeded ${timeoutMs}ms`));
      }, timeoutMs);
      timer.unref?.();
      this.#pending.set(id, { resolve, reject, timer });
      this.#send(message, (error) => {
        if (error === undefined) return;
        const pending = this.#pending.get(id);
        if (pending === undefined) return;
        clearTimeout(pending.timer);
        this.#pending.delete(id);
        pending.reject(error);
      });
    });
  }

  async #connect(): Promise<void> {
    if (this.#connected()) return;
    if (this.#connecting !== undefined) return await this.#connecting;
    this.#connecting = (async () => {
      let launched = false;
      let lastError: Error | undefined;
      for (let attempt = 0; attempt < 60; attempt++) {
        if (this.#closed) throw new Error("Mentis sidecar client is closed");
        try {
          const socket = await this.#connectOnce();
          this.#attach(socket);
          return;
        } catch (error) {
          lastError = error instanceof Error ? error : new Error(String(error));
          if (!launched) {
            launched = true;
            const entry = fileURLToPath(new URL("./sidecar.js", import.meta.url));
            this.#launchSidecar(entry, this.#endpoint);
          }
          await new Promise<void>((resolve) =>
            setTimeout(resolve, Math.min(250, 20 + attempt * 10)),
          );
        }
      }
      throw new Error(
        `Mentis sidecar singleton at ${this.#endpoint} did not become ready${
          lastError === undefined ? "" : `: ${lastError.message}`
        }`,
      );
    })().finally(() => {
      this.#connecting = undefined;
    });
    return await this.#connecting;
  }

  async #connectOnce(): Promise<Socket> {
    return await new Promise<Socket>((resolve, reject) => {
      const socket = this.#connectSocket(this.#endpoint);
      const timer = setTimeout(() => {
        cleanup();
        socket.destroy();
        reject(new Error("Mentis sidecar connection timed out"));
      }, 250);
      const cleanup = () => {
        clearTimeout(timer);
        socket.off("connect", onConnect);
        socket.off("error", onError);
      };
      const onConnect = () => {
        cleanup();
        resolve(socket);
      };
      const onError = (error: Error) => {
        cleanup();
        socket.destroy();
        reject(error);
      };
      socket.once("connect", onConnect);
      socket.once("error", onError);
    });
  }

  #attach(socket: Socket): void {
    this.#socket = socket;
    this.#decoder = new SidecarFrameDecoder();
    this.#ready = false;
    this.#initialized = false;
    socket.setNoDelay(true);
    socket.on("data", (chunk: Buffer) => this.#handleData(chunk));
    socket.on("error", (error) => this.#onWarning(`Mentis sidecar error: ${error.message}`));
    socket.on("close", () => this.#handleClose(socket));
  }

  #handleClose(socket: Socket): void {
    if (this.#socket !== socket) return;
    this.#socket = undefined;
    this.#serverPid = undefined;
    this.#ready = false;
    this.#initialized = false;
    this.#activeSessionKey = undefined;
    this.#lastSessionOpenResult = undefined;
    this.#rejectPending(new Error("Mentis sidecar connection closed"));
    if (!this.#closed && this.#desiredRunning) {
      this.#onWarning("Mentis sidecar stopped unexpectedly; automatic restart is scheduled.");
      this.#scheduleRestart();
    }
  }

  #connected(): boolean {
    return this.#socket !== undefined && !this.#socket.destroyed && this.#socket.writable;
  }

  #scheduleRestart(): void {
    if (this.#closed || !this.#desiredRunning || this.#restartTimer !== undefined) return;
    this.#restartAttempt++;
    this.#setState("restarting");
    const delay = Math.min(
      this.#restartMaxDelayMs,
      this.#restartBaseDelayMs * 2 ** Math.min(6, this.#restartAttempt - 1),
    );
    this.#restartTimer = setTimeout(() => {
      this.#restartTimer = undefined;
      void this.#ensureRunning().catch((error) => {
        if (this.#closed || !this.#desiredRunning) return;
        this.#onWarning(
          `Mentis sidecar restart failed: ${error instanceof Error ? error.message : String(error)}`,
        );
        this.#scheduleRestart();
      });
    }, delay);
    this.#restartTimer.unref?.();
  }

  #clearRestartTimer(): void {
    if (this.#restartTimer !== undefined) clearTimeout(this.#restartTimer);
    this.#restartTimer = undefined;
  }

  #flushNotifications(): void {
    if (!this.#ready) return;
    const queued = this.#notificationBacklog.splice(0);
    for (const notification of queued) this.notify(notification);
  }

  #send(message: SidecarOutboundMessage, onError?: (error?: Error) => void): void {
    const socket = this.#socket;
    if (!this.#connected() || socket === undefined) {
      onError?.(new Error("Mentis sidecar is not connected"));
      return;
    }
    try {
      socket.write(encodeSidecarFrame(message), (error) => onError?.(error ?? undefined));
    } catch (error) {
      onError?.(error instanceof Error ? error : new Error(String(error)));
    }
  }

  #handleData(chunk: Buffer): void {
    try {
      for (const message of this.#decoder.push(chunk)) {
        this.#handle(message as SidecarInboundMessage);
      }
    } catch (error) {
      this.#socket?.destroy(error instanceof Error ? error : new Error(String(error)));
    }
  }

  #handle(message: SidecarInboundMessage): void {
    if (message.protocolVersion !== SIDECAR_PROTOCOL_VERSION) {
      this.#onWarning("Mentis sidecar protocol mismatch");
      return;
    }
    if (message.type === "response") {
      const pending = this.#pending.get(message.id);
      if (pending === undefined) return;
      clearTimeout(pending.timer);
      this.#pending.delete(message.id);
      if (message.error === undefined) pending.resolve(message.result);
      else pending.reject(new Error(message.error));
      return;
    }
    this.#handleEvent(message);
  }

  #handleEvent(message: SidecarEventMessage): void {
    if (message.event.name === "ready") {
      this.#serverPid = message.event.pid;
      return;
    }
    if (message.event.name === "capsule.updated") {
      this.#onCapsule(message.event.capsule);
      return;
    }
    if (message.event.name === "active-context.updated") {
      this.#onActiveContext(message.event.clientSessionId, message.event.snapshot);
      return;
    }
    if (message.event.name === "context.updated") {
      this.#onScopeContext(message.event.scopeContext);
      return;
    }
    if (message.event.name === "warning") {
      this.#onWarning(message.event.message);
      return;
    }
    if (message.event.name === "cognition.cancel") {
      this.#reasonControllers.get(message.event.requestId)?.abort("Sidecar cognition cancelled");
      return;
    }
    if (message.event.name === "reason.request") void this.#reason(message.event);
    else void this.#cognition(message.event);
  }

  async #cognition(
    event: Extract<SidecarEventMessage["event"], { name: "cognition.request" }>,
  ): Promise<void> {
    const context = this.#reasonContext;
    const reasoner = context === undefined ? undefined : createPiCognitionReasoner(context);
    if (reasoner === undefined) {
      this.notify({
        method: "cognition.response",
        params: { requestId: event.requestId, error: "Pi model unavailable" },
      });
      return;
    }
    const controller = new AbortController();
    this.#reasonControllers.set(event.requestId, controller);
    try {
      const result = await reasoner.complete(
        { task: event.task, payload: event.payload, maxOutputTokens: event.maxOutputTokens },
        controller.signal,
      );
      this.notify({ method: "cognition.response", params: { requestId: event.requestId, result } });
    } catch (error) {
      this.notify({
        method: "cognition.response",
        params: {
          requestId: event.requestId,
          error: error instanceof Error ? error.message : String(error),
        },
      });
    } finally {
      this.#reasonControllers.delete(event.requestId);
    }
  }

  async #reason(event: Extract<SidecarEventMessage["event"], { name: "reason.request" }>) {
    const context = this.#reasonContext;
    const reasoner =
      context === undefined ? undefined : createPiPairwiseRelationshipReasoner(context);
    if (reasoner === undefined) {
      this.notify({
        method: "reason.response",
        params: { requestId: event.requestId, error: "Pi model unavailable" },
      });
      return;
    }
    const controller = new AbortController();
    this.#reasonControllers.set(event.requestId, controller);
    try {
      const result = await reasoner.judge(
        event.incomingContent,
        event.candidate,
        controller.signal,
      );
      this.notify({
        method: "reason.response",
        params: { requestId: event.requestId, result },
      });
    } catch (error) {
      this.notify({
        method: "reason.response",
        params: {
          requestId: event.requestId,
          error: error instanceof Error ? error.message : String(error),
        },
      });
    } finally {
      this.#reasonControllers.delete(event.requestId);
    }
  }

  #rejectPending(error: Error): void {
    for (const pending of this.#pending.values()) {
      clearTimeout(pending.timer);
      pending.reject(error);
    }
    this.#pending.clear();
  }

  #setState(state: MentisSidecarLifecycleState): void {
    if (this.#state === state) return;
    this.#state = state;
    this.#onStateChange(state);
  }
}
